// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';

import type { SolidSlab } from '../../api/types';
import type { GeometriEditor, GestureStatus } from '../../app/bygning/GeometriEditorContext';
import { useCanEdit } from '../../app/CaseRoleContext';
import { kindOf } from '../../app/keyTargets';
import { guideSnaps } from '../../bygning/cadMeasure';
import { slabLabel } from '../../bygning/geometri';
import { lengthInput } from '../../bygning/geometriEdit';
import { flushKeys, resolveKeys, toolHint } from '../../bygning/geometriKeys';
import { infer, manhattanAngle, type Inference, type SnapSegment } from '../../bygning/inference';
import type { XY } from '../../bygning/planWalls';
import { moveVertex, roomGrips, type PolygonEdit, type RoomGrip } from '../../bygning/roomGrips';
import { closesPolygon } from '../../bygning/roomTool';
import { addHoleOps, createSlabOps, isSlabRow, slabFromRoomOps, slabsAt } from '../../bygning/slabEdit';
import { formatAreaDa, ringArea, spaceAt } from '../../bygning/spaces';
import type { ViewportHandle } from '../../viewport/Viewport';
import { Glyph, ScreenTip } from './GeometriLayer';
import styles from './GeometriLayer.module.css';
import room from './RoomLayer.module.css';
import own from './SlabLayer.module.css';

const SNAP_PX = 12;
const CLOSE_PX = 10;
const DRAG_PX = 4;
/** A slab with more corners than this shows none as grips. */
const MAX_GRIPS = 60;

export interface SlabLayerProps {
  viewport: RefObject<ViewportHandle | null>;
  host: RefObject<HTMLElement | null>;
  editor: GeometriEditor;
  storeyId: number | null;
  z: number;
}

interface Drag {
  grip: RoomGrip;
  slab: SolidSlab;
  moved: boolean;
  inf: Inference | null;
  edit: PolygonEdit | null;
}

/**
 * The Dæk / gulv tool (G) on the locked plan (frontend-u0o.7, slice S7):
 * Tegn polygon draws a slab corner by corner with the inference engine
 * (wall faces, traces, guides), closed on its first corner or with Enter;
 * Fra rum lays one under (or over) the room clicked; Hul draws a hole in
 * the selected slab. While the tool is on, the storey's slabs are outlined
 * with their holes. A selected slab row shows its corners as grips.
 */
export function SlabLayer({ viewport, host: hostEl, editor, storeyId, z }: SlabLayerProps) {
  const [, setTick] = useState(0);
  const layerRef = useRef<HTMLDivElement>(null);
  const [pointer, setPointerState] = useState<{ raw: XY; inf: Inference | null } | null>(null);
  const [points, setPointsState] = useState<XY[]>([]);
  const [drag, setDragState] = useState<Drag | null>(null);
  const live = useRef({ editor, storeyId, z, pointer, points, drag });
  live.current = { editor, storeyId, z, pointer, points, drag };
  const setPointer = (p: typeof pointer) => {
    live.current.pointer = p;
    setPointerState(p);
  };
  const setPoints = (p: XY[]) => {
    live.current.points = p;
    setPointsState(p);
  };
  const setDrag = (d: Drag | null) => {
    live.current.drag = d;
    setDragState(d);
  };

  const canEdit = useCanEdit();
  const model = editor.model;
  const toolOn = editor.tool === 'floor' && editor.modify === null && canEdit && storeyId !== null;
  const mode = editor.slabOptions.mode;

  useEffect(() => {
    let frameId: number | null = null;
    const bump = () => {
      if (frameId !== null) return;
      frameId = requestAnimationFrame(() => {
        frameId = null;
        setTick((t) => t + 1);
      });
    };
    const off = viewport.current?.onCameraChange(bump) ?? (() => {});
    bump();
    return () => {
      off();
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, [viewport]);

  const toScreen = useCallback(
    (p: readonly number[]): [number, number] | null => {
      const s = viewport.current?.projectWorld(new Float64Array([p[0], p[1], live.current.z]), { clip: false });
      return s && Number.isFinite(s[0]) && Number.isFinite(s[1]) ? [s[0], s[1]] : null;
    },
    [viewport],
  );
  const metresPer = useCallback(
    (px: number): number => {
      const a = viewport.current?.planPoint(0, 0, live.current.z);
      const b = viewport.current?.planPoint(px, 0, live.current.z);
      return a && b ? Math.hypot(b[0] - a[0], b[1] - a[1]) : 0.1;
    },
    [viewport],
  );
  const planAtClient = useCallback(
    (clientX: number, clientY: number): XY | null => {
      const r = layerRef.current?.getBoundingClientRect();
      if (!r) return null;
      const hit = viewport.current?.planPoint(clientX - r.left, clientY - r.top, live.current.z);
      return hit ? [hit[0], hit[1]] : null;
    },
    [viewport],
  );

  // A slab's corners sit on wall faces (both of them: a floor runs under the walls to the outer face).
  const snap = editor.snapTargets;
  const segments = useMemo<SnapSegment[]>(() => {
    const out: SnapSegment[] = [];
    for (const w of model.walls ?? []) {
      if (storeyId !== null && w.storey_id !== storeyId) continue;
      const L = Math.hypot(w.end[0] - w.start[0], w.end[1] - w.start[1]);
      if (!(L > 1e-6)) continue;
      const h = (w.thickness ?? 0) / 2;
      const n: XY = [(-(w.end[1] - w.start[1]) / L) * h, ((w.end[0] - w.start[0]) / L) * h];
      for (const k of h > 0 ? [1, -1] : [0]) {
        out.push({ a: [w.start[0] + n[0] * k, w.start[1] + n[1] * k], b: [w.end[0] + n[0] * k, w.end[1] + n[1] * k], source: 'wall', label: `Væg ${w.id} · flade`, guid: w.guid });
      }
    }
    for (const sl of model.slabs ?? []) {
      if (storeyId !== null && sl.storey_id !== storeyId) continue;
      const r = sl.polygon.outer;
      for (let i = 0; i < r.length; i += 1) out.push({ a: [r[i][0], r[i][1]], b: [r[(i + 1) % r.length][0], r[(i + 1) % r.length][1]], source: 'wall', label: `${slabLabel(sl)} · kant`, guid: sl.guid });
    }
    for (const t of snap?.plane_traces ?? []) out.push({ a: t.start, b: t.end, source: 'trace', label: `P-${t.plane_label}`, points: t.point_count });
    return out;
  }, [model.walls, model.slabs, snap, storeyId]);
  const guides = useMemo(() => guideSnaps(editor.annotations.guides, storeyId), [editor.annotations.guides, storeyId]);
  const axisAngle = useMemo(() => manhattanAngle(snap?.axis_angles, segments), [snap, segments]);
  const ctxRef = useRef({ segments, guides, axisAngle });
  ctxRef.current = { segments, guides, axisAngle };
  const inferAt = useCallback(
    (raw: XY, origin: XY | null, alt: boolean): Inference | null => {
      const c = ctxRef.current;
      return infer(raw, {
        segments: [...c.segments, ...c.guides.segments],
        points: c.guides.points,
        axisAngle: c.axisAngle,
        origin,
        toggles: live.current.editor.snap,
        tolerance: metresPer(SNAP_PX),
        suspended: alt,
      });
    },
    [metresPer],
  );

  useEffect(() => {
    setPoints([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor.tool, mode, storeyId]);

  const storeySlabs = useMemo(() => (model.slabs ?? []).filter((s) => storeyId === null || s.storey_id === storeyId), [model.slabs, storeyId]);
  const storeySpaces = useMemo(() => (model.spaces ?? []).filter((s) => storeyId === null || s.storey_id === storeyId), [model.spaces, storeyId]);
  const selectedSlab = editor.selection.length === 1 ? (storeySlabs.find((s) => s.guid === editor.selection[0]) ?? null) : null;

  const finish = useCallback(() => {
    const { editor: ed, storeyId: sid, points: pts } = live.current;
    if (sid === null) return;
    const m = ed.slabOptions.mode;
    if (m === 'hole') {
      const target = ed.selection.length === 1 ? ed.selection[0] : null;
      if (!target) {
        ed.notify('Vælg det dæk, hullet skal i (Tab under et klik når frem til dækket)');
        return;
      }
      const r = addHoleOps(ed.model, target, pts);
      if ('error' in r) return ed.notify(r.error);
      setPoints([]);
      void ed.commit(r.ops, r.label);
      return;
    }
    const guid = ed.newGuid();
    const r = createSlabOps(ed.model, pts, sid, ed.slabOptions, guid);
    if ('error' in r) return ed.notify(r.error);
    setPoints([]);
    void ed.commit(r.ops, r.label).then((ok) => ok && live.current.editor.select([guid]));
  }, []);

  const fromRoom = useCallback((p: XY) => {
    const { editor: ed } = live.current;
    const sp = spaceAt(storeySpaces, p[0], p[1]);
    if (!sp) return ed.notify('Klik i et rum');
    const guid = ed.newGuid();
    const r = slabFromRoomOps(ed.model, sp.guid, ed.slabOptions, guid);
    if ('error' in r) return ed.notify(r.error);
    void ed.commit(r.ops, r.label).then((ok) => ok && live.current.editor.select([guid]));
  }, [storeySpaces]);

  // ------------------------------------------------------- the pointer --
  useEffect(() => {
    const el = hostEl.current;
    if (!el || !toolOn) return;
    const originOf = () => {
      const pts = live.current.points;
      return pts.length > 0 ? pts[pts.length - 1] : null;
    };
    const snapped = (e: PointerEvent) => {
      const raw = planAtClient(e.clientX, e.clientY);
      if (!raw) return null;
      const drawingNow = live.current.editor.slabOptions.mode !== 'room';
      return { raw, inf: drawingNow ? inferAt(raw, originOf(), e.altKey) : null };
    };
    const onMove = (e: PointerEvent) => {
      const s = snapped(e);
      if (!s) return;
      setPointer(s);
      live.current.editor.cursor.set(s.inf?.point ?? s.raw);
    };
    const onDown = (e: PointerEvent) => {
      if (e.button !== 0 || !(e.target instanceof HTMLCanvasElement)) return;
      e.stopPropagation();
      e.preventDefault();
      const s = snapped(e);
      if (!s) return;
      const ed = live.current.editor;
      if (ed.slabOptions.mode === 'room') {
        fromRoom(s.raw);
        return;
      }
      // Hul without a slab picked: the first click picks the slab under it.
      if (ed.slabOptions.mode === 'hole' && live.current.points.length === 0) {
        const picked = ed.selection.length === 1 ? (ed.model.slabs ?? []).find((x) => x.guid === ed.selection[0]) : undefined;
        if (!picked) {
          const under = slabsAt(ed.model, s.raw, live.current.storeyId)[0];
          if (under) ed.select([under.guid]);
          else ed.notify('Klik i det dæk, hullet skal i');
          return;
        }
      }
      const p = s.inf?.point ?? s.raw;
      const pts = live.current.points;
      if (closesPolygon(pts, p, metresPer(CLOSE_PX))) finish();
      else setPoints([...pts, p]);
    };
    const onLeave = () => setPointer(null);
    el.addEventListener('pointermove', onMove, true);
    el.addEventListener('pointerdown', onDown, true);
    el.addEventListener('pointerleave', onLeave);
    const prev = el.style.cursor;
    el.style.cursor = 'crosshair';
    return () => {
      el.removeEventListener('pointermove', onMove, true);
      el.removeEventListener('pointerdown', onDown, true);
      el.removeEventListener('pointerleave', onLeave);
      el.style.cursor = prev;
      setPointer(null);
    };
  }, [hostEl, toolOn, planAtClient, inferAt, metresPer, finish, fromRoom]);

  // ------------------------------------------------------- the keys --
  useEffect(() => {
    if (!toolOn) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target instanceof HTMLElement ? e.target : null;
      if (target?.closest('input, textarea, select, [role="dialog"], [role="menu"]')) return;
      const { editor: ed, points: pts } = live.current;
      const stop = () => {
        e.preventDefault();
        e.stopPropagation();
      };
      const plain = !e.metaKey && !e.ctrlKey && !e.altKey;
      if (e.key === 'Escape') {
        stop();
        if (pts.length > 0) setPoints([]);
        else ed.setTool('select');
        return;
      }
      if (e.key === 'Enter') {
        stop();
        if (pts.length >= 3) finish();
        return;
      }
      if (e.key === 'Backspace' && plain && pts.length > 0) {
        stop();
        setPoints(pts.slice(0, -1));
        return;
      }
      const out = resolveKeys({ key: e.key, metaKey: e.metaKey, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, altKey: e.altKey, kind: kindOf(target) }, '');
      const cmds = out.buffer ? [flushKeys(out.buffer)].filter((c) => c !== null) : out.commands;
      const run = cmds.filter((c) => c !== 'escape' && c !== 'cycle' && c !== 'cycle-back');
      if (run.length > 0 || (e.key.length === 1 && plain)) stop();
      for (const c of run) ed.run(c);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [toolOn, finish]);

  // ------------------------------------------- a selected slab's corners --
  const gripSlab = editor.tool === 'select' && !editor.modify && canEdit && selectedSlab && isSlabRow(selectedSlab) ? selectedSlab : null;
  // A detected slab follows the scan's outline: hundreds of corners are no grips, only noise.
  const corners = gripSlab ? gripSlab.polygon.outer.length + gripSlab.polygon.holes.reduce((n, h) => n + h.length, 0) : 0;
  const grips = gripSlab && corners <= MAX_GRIPS ? roomGrips(gripSlab, metresPer(18)).filter((g) => g.kind === 'vertex') : [];
  const startGrip = (grip: RoomGrip, sl: SolidSlab, e: ReactPointerEvent) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const x0 = e.clientX;
    const y0 = e.clientY;
    setDrag({ grip, slab: sl, moved: false, inf: null, edit: null });
    const move = (m: PointerEvent) => {
      const cur = live.current.drag;
      if (!cur) return;
      if (!cur.moved && Math.hypot(m.clientX - x0, m.clientY - y0) < DRAG_PX) return;
      const raw = planAtClient(m.clientX, m.clientY);
      if (!raw) return;
      const ring = cur.grip.ring < 0 ? cur.slab.polygon.outer : cur.slab.polygon.holes[cur.grip.ring];
      const prev = ring[(cur.grip.index + ring.length - 1) % ring.length];
      const inf = inferAt(raw, [prev[0], prev[1]], m.altKey);
      setDrag({ ...cur, moved: true, inf, edit: moveVertex(cur.slab, cur.grip.ring, cur.grip.index, inf?.point ?? raw) });
    };
    const end = () => {
      window.removeEventListener('pointermove', move, true);
      window.removeEventListener('pointerup', up, true);
      window.removeEventListener('keydown', esc, true);
    };
    const up = () => {
      end();
      const cur = live.current.drag;
      setDrag(null);
      if (!cur?.moved || !cur.edit) return;
      if ('error' in cur.edit) return live.current.editor.notify(cur.edit.error);
      void live.current.editor.commit([{ op: 'slab.update', guid: cur.slab.guid, set: { polygon: cur.edit.polygon } }], `Flyt hjørne · ${slabLabel(cur.slab)}`);
    };
    const esc = (k: KeyboardEvent) => {
      if (k.key !== 'Escape') return;
      k.preventDefault();
      k.stopPropagation();
      end();
      setDrag(null);
    };
    window.addEventListener('pointermove', move, true);
    window.addEventListener('pointerup', up, true);
    window.addEventListener('keydown', esc, true);
  };

  // ------------------------------------------------------ status line --
  const drawPoint = pointer ? (pointer.inf?.point ?? pointer.raw) : null;
  const gesture = useMemo<GestureStatus | null>(() => {
    if (drag?.moved) return { hint: { lead: 'Hjørne · slip for at sætte', keys: [{ key: 'Alt', text: 'uden snap' }, { key: 'Esc', text: 'annullér' }] }, readout: null };
    if (toolOn && mode !== 'room' && points.length > 0) {
      const ring = drawPoint ? [...points, drawPoint] : points;
      const last = points[points.length - 1];
      const seg = drawPoint ? Math.hypot(drawPoint[0] - last[0], drawPoint[1] - last[1]) : 0;
      return {
        hint: {
          lead: points.length >= 3 ? `${mode === 'hole' ? 'Hul' : 'Dæk'} · klik det første hjørne eller ↵ for at lukke` : `${mode === 'hole' ? 'Hul' : 'Dæk'} · klik næste hjørne`,
          keys: [{ key: '↵', text: 'luk' }, { key: '⌫', text: 'fortryd hjørne' }, { key: 'Alt', text: 'uden snap' }, { key: 'Esc', text: 'ryd' }],
        },
        readout: `${lengthInput(seg)} m · ${ring.length >= 3 ? formatAreaDa(Math.abs(ringArea(ring))) : `${points.length} hjørne${points.length === 1 ? '' : 'r'}`}`,
      };
    }
    return null;
  }, [drag, toolOn, mode, points, drawPoint]);
  useEffect(() => {
    if (!toolOn && !drag) return;
    editor.gesture.set(gesture ?? (toolOn ? { hint: toolHint('floor', editor.selection.length, 'line', 'place', mode), readout: null } : null));
  }, [editor.gesture, gesture, toolOn, drag, editor.selection.length, mode]);
  useEffect(() => {
    if (!toolOn) return;
    return () => live.current.editor.gesture.set(null);
  }, [toolOn]);

  // ------------------------------------------------------------- draw --
  const ringPts = (ring: readonly (readonly number[])[]): string | null => {
    const out: string[] = [];
    for (const p of ring) {
      const s = toScreen(p);
      if (!s) return null;
      out.push(`${s[0].toFixed(1)},${s[1].toFixed(1)}`);
    }
    return out.join(' ');
  };
  const polyPath = (poly: { outer: readonly (readonly number[])[]; holes?: readonly (readonly (readonly number[])[])[] }): string | null => {
    const parts: string[] = [];
    for (const r of [poly.outer, ...(poly.holes ?? [])]) {
      const d = ringPts(r);
      if (!d) return null;
      parts.push(`M${d.split(' ').join('L')}Z`);
    }
    return parts.join('');
  };

  const showSlabs = toolOn || gripSlab !== null;
  const hoverRoom = toolOn && mode === 'room' && pointer ? spaceAt(storeySpaces, pointer.raw[0], pointer.raw[1]) : null;
  const hoverSlab = toolOn && mode === 'hole' && editor.selection.length !== 1 && pointer ? (slabsAt(model, pointer.raw, storeyId)[0] ?? null) : null;
  const firstClose = toolOn && mode !== 'room' && points.length >= 3 && drawPoint ? closesPolygon(points, drawPoint, metresPer(CLOSE_PX)) : false;
  const shownInf = drag?.moved ? drag.inf : toolOn && mode !== 'room' ? (pointer?.inf ?? null) : null;
  const infAt = shownInf && shownInf.kind !== 'free' ? toScreen(shownInf.point) : null;
  const infGuide = shownInf?.guide ? ringPts([shownInf.guide[0], shownInf.guide[1]]) : null;
  const dragPoly = drag?.moved && drag.edit && 'polygon' in drag.edit ? drag.edit.polygon : null;

  if (!showSlabs && !drag) return null;
  return (
    <div ref={layerRef} className={styles.layer}>
      <svg className={styles.svg} aria-hidden="true">
        {showSlabs &&
          storeySlabs.map((sl) => {
            const d = polyPath(sl.polygon);
            if (!d) return null;
            const on = editor.selection.includes(sl.guid);
            return <path key={sl.guid} d={d} className={on ? own.slabOn : sl.kind === 'floor' ? own.slab : own.slabAbove} />;
          })}
        {hoverSlab && (() => {
          const d = polyPath(hoverSlab.polygon);
          return d ? <path d={d} className={room.hover} /> : null;
        })()}
        {hoverRoom && (() => {
          const d = polyPath(hoverRoom.polygon);
          return d ? <path d={d} className={room.draftNew} /> : null;
        })()}
        {toolOn && mode !== 'room' && points.length > 0 && (() => {
          const ring = drawPoint ? [...points, firstClose ? points[0] : drawPoint] : points;
          const d = ringPts(ring);
          const fill = ring.length >= 3 ? ringPts(firstClose ? points : ring) : null;
          return (
            <g>
              {fill && <polygon points={fill} className={mode === 'hole' ? own.holeFill : room.drawFill} />}
              {d && <polyline points={d} className={styles.chainLine} />}
              {points.map((p, i) => {
                const s = toScreen(p);
                return s ? <circle key={i} cx={s[0]} cy={s[1]} r={i === 0 && firstClose ? 7 : 3.5} className={i === 0 && firstClose ? room.closeDot : styles.chainPoint} /> : null;
              })}
            </g>
          );
        })()}
        {dragPoly && (() => {
          const d = polyPath(dragPoly);
          return d ? <path d={d} className={room.draftChanged} /> : null;
        })()}
        {infGuide && shownInf && <polyline points={infGuide} className={shownInf.kind === 'trace' ? `${styles.guide} ${styles.traceGuide}` : styles.guide} />}
        {gripSlab &&
          grips.map((g) => {
            const s = toScreen(g.at);
            if (!s) return null;
            const on = drag?.grip.index === g.index && drag.grip.ring === g.ring;
            return (
              <circle key={`${g.ring}:${g.index}`} cx={s[0]} cy={s[1]} r={5} className={on ? `${styles.grip} ${styles.gripOn}` : styles.grip} onPointerDown={(e) => startGrip(g, gripSlab, e)}>
                <title>Træk dækkets hjørne</title>
              </circle>
            );
          })}
        {infAt && shownInf && <Glyph kind={shownInf.kind} at={infAt} />}
      </svg>
      {hoverRoom && pointer && (() => {
        const at = toScreen(pointer.raw);
        return at ? (
          <span className={room.chip} style={{ transform: `translate(${(at[0] + 14).toFixed(1)}px, ${(at[1] + 14).toFixed(1)}px)` }}>
            {editor.slabOptions.kind === 'floor' ? 'Dæk under' : editor.slabOptions.kind === 'ceiling' ? 'Loft over' : 'Tag over'} {hoverRoom.name || 'rummet'} · klik
          </span>
        ) : null;
      })()}
      {infAt && shownInf?.tip && <ScreenTip inf={shownInf} at={infAt} />}
    </div>
  );
}
